import { CheckRounded, CloseRounded } from "@mui/icons-material";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useUser from "../../../../providers/user/user_provider";
import { ConfirmationDialog } from "../../../../components/common/confirmation-dialog";
import { JobApplicationModel } from "../../model/job_application_model";
import {
  TableCell,
  TableEmptyState,
  TableHeaderRow,
  TableRow,
} from "../../../users/components/table_toolkit/table_toolkit";
import { black, green, greenLight, greyEnabled, purple, red, redLight } from "../../../../theme/colors";

interface DataGridInterface {
  data: JobApplicationModel[];
  page: number;
  pageSize: number;
  setSelectedApplication: (val: JobApplicationModel) => void;
  updateServiceApiCall: (params: { statusId: number; jobId: number; comments: string }) => void;
  setRejectDialogOpen: (val: boolean) => void;
}

const HEADER_LABELS = [
  { label: "Sl. No.", flex: 0.35 },
  { label: "Applicant", flex: 1.5 },
  { label: "Qualification", flex: 0.9 },
  { label: "DOB", flex: 0.6 },
  { label: "Contact", flex: 1.2 },
  { label: "Company / Person", flex: 1 },
  { label: "Location", flex: 1 },
  { label: "Status", flex: 0.8 },
  { label: "Applied On", flex: 0.7 },
  { label: "Actions" },
];

function statusColor(status: string) {
  if (status === "In Review") return purple;
  if (status === "Application Forwarded" || status === "Shortlisted") return green;
  return red;
}

export default function DataGridJobApplication({
  data,
  page,
  pageSize,
  setSelectedApplication,
  updateServiceApiCall,
  setRejectDialogOpen,
}: DataGridInterface) {
  const navigate = useNavigate();
  const { hasPermission } = useUser();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<JobApplicationModel>();

  const handleApproveConfirm = () => {
    if (selectedItem) {
      updateServiceApiCall({ statusId: 2, jobId: +selectedItem.id, comments: "" });
    }
    setConfirmOpen(false);
  };

  function actionButtons(item: JobApplicationModel) {
    if (!hasPermission("jobs", "update")) return null;
    const status = item.applicationStatus;

    if (status === "In Review") {
      return (
        <Box display="flex" gap={0.5}>
          <Tooltip title="Approve">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedItem(item);
                setConfirmOpen(true);
              }}
              sx={{ bgcolor: greenLight, "&:hover": { bgcolor: "#D6EEE0" } }}
            >
              <CheckRounded sx={{ color: green, fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reject">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedApplication(item);
                setRejectDialogOpen(true);
              }}
              sx={{ bgcolor: redLight, "&:hover": { bgcolor: "#FBD9D6" } }}
            >
              <CloseRounded sx={{ color: red, fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    return (
      <Box display="flex" gap={0.5}>
        <Tooltip title={status}>
          <span>
            <IconButton disabled sx={{ bgcolor: status === "Rejected" ? redLight : greenLight, opacity: 0.5 }}>
              {status === "Rejected" ? (
                <CloseRounded sx={{ color: red, fontSize: 20 }} />
              ) : (
                <CheckRounded sx={{ color: green, fontSize: 20 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  }

  if (data.length === 0) {
    return <TableEmptyState message="No job applications found" hint="Try adjusting your search or filters." />;
  }

  return (
    <Box mt={1}>
      <TableHeaderRow labels={HEADER_LABELS} lastColumnWidth={100} bordered />
      {data.map((item, index) => (
        <TableRow key={item.id} bordered onClick={() => navigate(`/jobs/jobsApplicationDetails/${item.id}`)}>
          <TableCell label="Sl. No." flex={0.35} bordered>
            <Typography fontSize={14} color={black} noWrap>
              {(page - 1) * pageSize + index + 1}
            </Typography>
          </TableCell>
          <TableCell label="Applicant" flex={1.5} bordered>
            <Typography fontWeight={600} color={black} noWrap>
              {item.name || "-"}
            </Typography>
            <Typography variant="body2" color={greyEnabled} noWrap>
              {item.emailId || "-"}
            </Typography>
          </TableCell>
          <TableCell label="Qualification" flex={0.9} bordered>
            <Typography fontSize={14} color={black} noWrap>
              {item.education || "-"}
            </Typography>
          </TableCell>
          <TableCell label="DOB" flex={0.6} bordered>
            <Typography fontSize={14} color={black} noWrap>
              {item.dob || "-"}
            </Typography>
          </TableCell>
          <TableCell label="Contact" flex={1.2} bordered>
            <Typography fontSize={14} color={black} noWrap>
              {item.mobileNumber || "-"}
            </Typography>
          </TableCell>
          <TableCell label="Company / Person" flex={1} bordered>
            <Typography fontSize={14} color={black} noWrap>
              {item.company || "-"}
            </Typography>
          </TableCell>
          <TableCell label="Location" flex={1} bordered>
            <Typography fontSize={14} color={black} noWrap>
              {[item.district, item.state].filter(Boolean).join(", ") || "-"}
            </Typography>
          </TableCell>
          <TableCell label="Status" flex={0.8} bordered>
            <Typography fontSize={14} fontWeight={600} color={statusColor(item.applicationStatus)} noWrap>
              {item.applicationStatus}
            </Typography>
          </TableCell>
          <TableCell label="Applied On" flex={0.7} bordered>
            <Typography fontSize={14} color={black} noWrap>
              {item.createdAt ? item.createdAt.split("T")[0] : "-"}
            </Typography>
          </TableCell>
          <TableCell isLast lastColumnWidth={100} bordered>
            <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }}>
              {actionButtons(item)}
            </Box>
          </TableCell>
        </TableRow>
      ))}
      <ConfirmationDialog
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleApproveConfirm}
        open={confirmOpen}
        selectedItem={selectedItem}
        bodyContent="Are you sure want to approve this applicant?"
      />
    </Box>
  );
}
